/**
 * Formula evaluator — calculated-field expression engine.
 *
 * Deterministic recursive-descent parser + tree-walking evaluator. Replaces the
 * previous `new Function(...)` eval (unsafe, and unable to host helper functions).
 *
 * THE GRAMMAR AND SEMANTICS HERE ARE A CONTRACT shared with the Dart
 * implementation in `apps/mobile/lib/form_engine/calc/`. Both sides are exercised
 * by `shared/form-definitions/formula-vectors.json` so web and mobile can never
 * silently disagree on the same form definition.
 *
 * Grammar (lowest → highest precedence):
 *   comparison := additive (("=="|"!="|">"|"<"|">="|"<=") additive)?
 *   additive   := multiplicative (("+"|"-") multiplicative)*
 *   multiplic. := power (("*"|"/") power)*
 *   power      := unary ("^" power)?        // right-associative, fractional ok
 *   unary      := "-" unary | primary
 *   primary    := number | string | ref | ident "(" args ")" | "(" comparison ")"
 *   ref        := "{" key "}"
 *
 * Functions: IF, ROUND, ABS, TODAY, YEARS_BETWEEN, IS_EMPTY, IS_FILLED.
 *
 * Value model: number | string | null. Booleans coerce to 1/0. A missing /
 * empty / non-numeric reference is `null`; arithmetic and ordered comparison
 * propagate null. `IS_EMPTY`/`IS_FILLED` inspect the value rather than
 * propagating, so they can detect an absent field.
 */

export type FormulaValue = number | string | null

// ─── AST ────────────────────────────────────────────────────────────────────

type Node =
  | { t: 'num'; v: number }
  | { t: 'str'; v: string }
  | { t: 'ref'; key: string }
  | { t: 'bin'; op: string; l: Node; r: Node }
  | { t: 'neg'; e: Node }
  | { t: 'call'; name: string; args: Node[] }

// ─── Lexer ──────────────────────────────────────────────────────────────────

type Tok =
  | { k: 'num'; v: number }
  | { k: 'str'; v: string }
  | { k: 'ref'; v: string }
  | { k: 'ident'; v: string }
  | { k: 'op'; v: string }
  | { k: 'lp' }
  | { k: 'rp' }
  | { k: 'comma' }

const TWO_CHAR = new Set(['==', '!=', '>=', '<='])
const ONE_CHAR = new Set(['+', '-', '*', '/', '^', '>', '<'])

function lex(src: string): Tok[] {
  const toks: Tok[] = []
  let i = 0
  while (i < src.length) {
    const c = src[i]
    if (c === ' ' || c === '\t' || c === '\n' || c === '\r') { i++; continue }
    if (c === '(') { toks.push({ k: 'lp' }); i++; continue }
    if (c === ')') { toks.push({ k: 'rp' }); i++; continue }
    if (c === ',') { toks.push({ k: 'comma' }); i++; continue }
    if (c === '{') {
      const end = src.indexOf('}', i)
      if (end === -1) throw new Error('unterminated ref')
      toks.push({ k: 'ref', v: src.slice(i + 1, end).trim() })
      i = end + 1
      continue
    }
    if (c === '"' || c === "'") {
      const end = src.indexOf(c, i + 1)
      if (end === -1) throw new Error('unterminated string')
      toks.push({ k: 'str', v: src.slice(i + 1, end) })
      i = end + 1
      continue
    }
    const two = src.slice(i, i + 2)
    if (TWO_CHAR.has(two)) { toks.push({ k: 'op', v: two }); i += 2; continue }
    if (ONE_CHAR.has(c)) { toks.push({ k: 'op', v: c }); i++; continue }
    if (/[0-9.]/.test(c)) {
      let j = i + 1
      while (j < src.length && /[0-9.]/.test(src[j])) j++
      const num = Number(src.slice(i, j))
      if (!isFinite(num)) throw new Error('bad number')
      toks.push({ k: 'num', v: num })
      i = j
      continue
    }
    if (/[A-Za-z_]/.test(c)) {
      let j = i + 1
      while (j < src.length && /[A-Za-z0-9_]/.test(src[j])) j++
      toks.push({ k: 'ident', v: src.slice(i, j) })
      i = j
      continue
    }
    throw new Error(`unexpected char '${c}'`)
  }
  return toks
}

// ─── Parser ─────────────────────────────────────────────────────────────────

const COMPARE_OPS = new Set(['==', '!=', '>', '<', '>=', '<='])

class Parser {
  private pos = 0
  constructor(private toks: Tok[]) {}

  private peek(): Tok | undefined { return this.toks[this.pos] }
  private next(): Tok {
    const t = this.toks[this.pos]
    if (!t) throw new Error('unexpected end')
    this.pos++
    return t
  }
  expectEnd(): void {
    if (this.pos !== this.toks.length) throw new Error('trailing tokens')
  }

  parseExpression(): Node { return this.parseComparison() }

  private parseComparison(): Node {
    let left = this.parseAdditive()
    const t = this.peek()
    if (t && t.k === 'op' && COMPARE_OPS.has(t.v)) {
      this.next()
      const right = this.parseAdditive()
      left = { t: 'bin', op: t.v, l: left, r: right }
    }
    return left
  }

  private parseAdditive(): Node {
    let left = this.parseMultiplicative()
    for (;;) {
      const t = this.peek()
      if (t && t.k === 'op' && (t.v === '+' || t.v === '-')) {
        this.next()
        left = { t: 'bin', op: t.v, l: left, r: this.parseMultiplicative() }
      } else break
    }
    return left
  }

  private parseMultiplicative(): Node {
    let left = this.parsePower()
    for (;;) {
      const t = this.peek()
      if (t && t.k === 'op' && (t.v === '*' || t.v === '/')) {
        this.next()
        left = { t: 'bin', op: t.v, l: left, r: this.parsePower() }
      } else break
    }
    return left
  }

  private parsePower(): Node {
    const left = this.parseUnary()
    const t = this.peek()
    if (t && t.k === 'op' && t.v === '^') {
      this.next()
      return { t: 'bin', op: '^', l: left, r: this.parsePower() } // right-assoc
    }
    return left
  }

  private parseUnary(): Node {
    const t = this.peek()
    if (t && t.k === 'op' && t.v === '-') {
      this.next()
      return { t: 'neg', e: this.parseUnary() }
    }
    return this.parsePrimary()
  }

  private parsePrimary(): Node {
    const t = this.next()
    if (t.k === 'num') return { t: 'num', v: t.v }
    if (t.k === 'str') return { t: 'str', v: t.v }
    if (t.k === 'ref') return { t: 'ref', key: t.v }
    if (t.k === 'lp') {
      const inner = this.parseComparison()
      const close = this.next()
      if (close.k !== 'rp') throw new Error('expected )')
      return inner
    }
    if (t.k === 'ident') {
      const open = this.next()
      if (open.k !== 'lp') throw new Error('expected ( after function')
      const args: Node[] = []
      if (this.peek()?.k !== 'rp') {
        args.push(this.parseComparison())
        while (this.peek()?.k === 'comma') {
          this.next()
          args.push(this.parseComparison())
        }
      }
      const close = this.next()
      if (close.k !== 'rp') throw new Error('expected )')
      return { t: 'call', name: t.v.toUpperCase(), args }
    }
    throw new Error('expected operand')
  }
}

// ─── Evaluator ──────────────────────────────────────────────────────────────

interface Ctx { values: Record<string, unknown>; now: Date }

function resolveRef(key: string, values: Record<string, unknown>): FormulaValue {
  const v = values[key]
  if (v === null || v === undefined || v === '') return null
  if (typeof v === 'number') return isFinite(v) ? v : null
  if (typeof v === 'boolean') return v ? 1 : 0
  if (typeof v === 'string') return v
  return null
}

function toNum(v: FormulaValue): number | null {
  if (v === null) return null
  if (typeof v === 'number') return isFinite(v) ? v : null
  const n = Number(v)
  return isNaN(n) || !isFinite(n) ? null : n
}

function truthy(v: FormulaValue): boolean {
  if (v === null) return false
  if (typeof v === 'number') return v !== 0
  return v.trim() !== ''
}

function parseDate(v: FormulaValue): Date | null {
  if (v === null) return null
  const s = String(v).trim()
  if (!s) return null
  const d = new Date(s.length <= 10 ? `${s}T00:00:00` : s)
  return isNaN(d.getTime()) ? null : d
}

function isoDate(d: Date): string {
  const y = d.getFullYear()
  const m = String(d.getMonth() + 1).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')
  return `${y}-${m}-${day}`
}

function wholeYearsBetween(d1: Date, d2: Date): number {
  let years = d2.getFullYear() - d1.getFullYear()
  if (
    d2.getMonth() < d1.getMonth() ||
    (d2.getMonth() === d1.getMonth() && d2.getDate() < d1.getDate())
  ) {
    years--
  }
  return years < 0 ? 0 : years
}

function evalCall(name: string, args: Node[], ctx: Ctx): FormulaValue {
  switch (name) {
    case 'IF': {
      if (args.length < 3) return null
      return truthy(evalNode(args[0], ctx))
        ? evalNode(args[1], ctx)
        : evalNode(args[2], ctx)
    }
    case 'ROUND': {
      if (args.length < 1) return null
      const x = toNum(evalNode(args[0], ctx))
      if (x === null) return null
      const d = args.length > 1 ? (toNum(evalNode(args[1], ctx)) ?? 0) : 0
      const factor = Math.pow(10, Math.trunc(d))
      return Math.round(x * factor) / factor
    }
    case 'ABS': {
      if (args.length < 1) return null
      const x = toNum(evalNode(args[0], ctx))
      return x === null ? null : Math.abs(x)
    }
    case 'TODAY':
      return isoDate(ctx.now)
    case 'YEARS_BETWEEN': {
      if (args.length < 1) return null
      const d1 = parseDate(evalNode(args[0], ctx))
      const d2 = args.length > 1 ? parseDate(evalNode(args[1], ctx)) : ctx.now
      if (!d1 || !d2) return null
      return wholeYearsBetween(d1, d2)
    }
    case 'IS_EMPTY': {
      if (args.length < 1) return 1
      const v = evalNode(args[0], ctx)
      return v === null || (typeof v === 'string' && v.trim() === '') ? 1 : 0
    }
    case 'IS_FILLED': {
      if (args.length < 1) return 0
      const v = evalNode(args[0], ctx)
      return v === null || (typeof v === 'string' && v.trim() === '') ? 0 : 1
    }
    default:
      return null
  }
}

function evalNode(node: Node, ctx: Ctx): FormulaValue {
  switch (node.t) {
    case 'num': return node.v
    case 'str': return node.v
    case 'ref': return resolveRef(node.key, ctx.values)
    case 'neg': {
      const v = toNum(evalNode(node.e, ctx))
      return v === null ? null : -v
    }
    case 'call': return evalCall(node.name, node.args, ctx)
    case 'bin': {
      const op = node.op
      const l = evalNode(node.l, ctx)
      const r = evalNode(node.r, ctx)
      if (op === '==' || op === '!=') {
        const ln = toNum(l), rn = toNum(r)
        let eq: boolean
        if (ln !== null && rn !== null) eq = ln === rn
        else eq = (l === null ? null : String(l)) === (r === null ? null : String(r))
        return (op === '==' ? eq : !eq) ? 1 : 0
      }
      if (op === '>' || op === '<' || op === '>=' || op === '<=') {
        const ln = toNum(l), rn = toNum(r)
        if (ln === null || rn === null) return null
        const res =
          op === '>' ? ln > rn :
          op === '<' ? ln < rn :
          op === '>=' ? ln >= rn : ln <= rn
        return res ? 1 : 0
      }
      // arithmetic
      const ln = toNum(l), rn = toNum(r)
      if (ln === null || rn === null) return null
      let out: number
      switch (op) {
        case '+': out = ln + rn; break
        case '-': out = ln - rn; break
        case '*': out = ln * rn; break
        case '/': if (rn === 0) return null; out = ln / rn; break
        case '^': out = Math.pow(ln, rn); break
        default: return null
      }
      return isFinite(out) ? out : null
    }
  }
}

// ─── Public surface ───────────────────────────────────────────────────────────

/**
 * Evaluate a formula to its full value (number | string | null). Used by
 * `computeCalculatedFields` so functions like IF can yield text results.
 */
export function evaluateFormulaValue(
  formula: string,
  values: Record<string, unknown>,
  now: Date = new Date(),
): FormulaValue {
  try {
    let src = formula.trim()
    if (src.startsWith('=')) src = src.slice(1)
    if (!src.trim()) return null
    const parser = new Parser(lex(src))
    const ast = parser.parseExpression()
    parser.expectEnd()
    return evalNode(ast, { values, now })
  } catch {
    return null
  }
}

/**
 * Numeric-only convenience wrapper (back-compat). Returns a finite number or
 * null. Used by the formula-builder live preview.
 */
export function evaluateFormula(
  formula: string,
  values: Record<string, unknown>,
): number | null {
  const v = evaluateFormulaValue(formula, values)
  return typeof v === 'number' && isFinite(v) ? v : null
}
